//#region libraries
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

// external libraries
import { Title, Subtitle, Flex, Toggle, ToggleItem } from '@tremor/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// custom components
import { NewScreen, Card, CustomSpinner } from '_atoms';
import { TableHeader } from '_molecules';

// bootstrap
import { Badge, Button, Form, Modal, Row, Col, Table } from 'react-bootstrap';

// services
import { getLanyardOrders, updateLanyardOrderStatus } from '_services/lanyard-orders-service';

// local
import DeskPagination from '../pax-requests/pagination';

// hooks
import useIdleLogout from '../../../hooks/use-idle-logout';

// styles
import { Colors } from '_styles';
//#endregion

const DEFAULT_PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 60 * 1000;
const IDLE_LOGOUT_MS = 6 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SEARCH_LENGTH = 60;
const AIRPORTS = ['DUB', 'ORK'];
const FULFILMENTS = [
  { value: 'collection', label: 'Collection' },
  { value: 'delivery', label: 'Delivery' },
];

const VIEWS = {
  placed: { label: 'Ordered', empty: 'No new lanyard orders.' },
  ready: { label: 'Ready', empty: 'No orders waiting to be collected or sent.' },
  completed: { label: 'Collected / Sent', empty: 'No collected or sent orders in the last 90 days.' },
  cancelled: { label: 'Cancelled', empty: 'No cancelled orders in the last 90 days.' },
};

// what each status can move to, and how the button and confirm dialog read.
// Collection: Ordered -> Ready to collect -> Collected. Delivery: Ordered -> Ready -> Sent.
const ACTIONS = {
  placed: [
    { status: 'ready', label: 'Mark ready', variant: 'primary' },
    { status: 'cancelled', label: 'Cancel', variant: 'outline-danger' },
  ],
  ready: [
    { status: 'completed', label: 'Mark collected', deliveryLabel: 'Mark sent', variant: 'primary' },
    { status: 'cancelled', label: 'Cancel', variant: 'outline-danger' },
  ],
};

// toast after a successful change
const DONE_LABELS = {
  ready: { collection: 'ready to collect', delivery: 'ready to send' },
  completed: { collection: 'collected', delivery: 'sent' },
  cancelled: { collection: 'cancelled', delivery: 'cancelled' },
};

const actionQuestion = (order, status) => {
  const who = order.passenger?.name || 'this passenger';
  const delivery = order.fulfilment === 'delivery';
  if (status === 'ready') {
    return delivery
      ? `Mark the order for ${who} as packed and ready to send? The passenger sees "Ready" in the app.`
      : `Mark the order for ${who} as ready to collect at ${order.iata}? They'll get an email and a push notification.`;
  }
  if (status === 'completed') {
    return delivery
      ? `Mark the order for ${who} as sent? They'll get an email and a push notification.`
      : `Mark the order for ${who} as collected?`;
  }
  return `Cancel the order for ${who}? The stock is put back and they'll get a push notification.`;
};

const actionLabel = (action, order) =>
  (order.fulfilment === 'delivery' && action.deliveryLabel) || action.label;

const formatDateTime = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatDay = (ymd) => {
  const date = new Date(`${ymd}T00:00:00`);
  if (!ymd || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Sunflower lanyard orders — the only page the LANYARD_ORDERS_DESK role can reach.
const LanyardOrders = () => {
  const history = useHistory();

  // shared-PC hardening: log out after 15 minutes without activity
  useIdleLogout(IDLE_LOGOUT_MS);

  //#region state
  const [view, setView] = useState('placed');
  const [iata, setIata] = useState('');
  const [fulfilment, setFulfilment] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pending, setPending] = useState({}); // { [id]: true }
  const [confirming, setConfirming] = useState(null); // { order, action }
  //#endregion

  const latestFetch = useRef(0);
  const loggingOut = useRef(false);
  const tableTop = useRef(null);

  // 401/403: the session is no longer valid for the desk — sign out
  const handleAuthError = useCallback((e) => {
    if (e?.status === 401 || e?.status === 403) {
      if (!loggingOut.current) {
        loggingOut.current = true;
        history.push('/logout');
      }
      return true;
    }
    return false;
  }, [history]);

  //#region data
  const fetchOrders = useCallback(async ({ quiet = false } = {}) => {
    const fetchId = ++latestFetch.current;
    if (!quiet) setLoading(true);

    try {
      const result = await getLanyardOrders({
        status: view,
        iata: iata || undefined,
        fulfilment: fulfilment || undefined,
        q: search || undefined,
        limit: pageSize,
        skip,
      });
      if (fetchId !== latestFetch.current) return; // a newer fetch has started
      // the page we were on no longer exists (orders moved on) - go to the last one
      if (skip > 0 && skip >= result.total) {
        setSkip(Math.max(0, Math.floor((result.total - 1) / pageSize) * pageSize));
        return;
      }
      setOrders(result.orders);
      setTotal(result.total);
      setError(null);
      setLastUpdated(new Date());
    } catch (e) {
      if (fetchId !== latestFetch.current) return;
      if (handleAuthError(e)) return;
      if (!quiet) setError(e?.message || 'Could not load lanyard orders.');
    } finally {
      if (fetchId === latestFetch.current && !quiet) setLoading(false);
    }
  }, [view, iata, fulfilment, search, skip, pageSize, handleAuthError]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setSkip(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // auto-refresh every 60s while the tab is visible; refresh on return to the tab
  useEffect(() => {
    let interval = null;
    const start = () => {
      clearInterval(interval);
      interval = setInterval(() => fetchOrders({ quiet: true }), AUTO_REFRESH_MS);
    };
    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        fetchOrders({ quiet: true });
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchOrders]);
  //#endregion

  //#region status
  const submitStatus = async () => {
    if (!confirming) return;
    const { order, action } = confirming;
    setConfirming(null);
    if (pending[order.id]) return;
    setPending((prev) => ({ ...prev, [order.id]: true }));

    try {
      await updateLanyardOrderStatus(order.id, action.status);
      // the order now belongs to another tab; reload so the page fills back up
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      setTotal((prev) => Math.max(0, prev - 1));
      fetchOrders({ quiet: true });
      toast.success(`${order.passenger?.name || 'Order'}: ${DONE_LABELS[action.status][order.fulfilment === 'delivery' ? 'delivery' : 'collection']}`);
    } catch (e) {
      if (handleAuthError(e)) return;
      toast.error(e?.message || 'Could not update the order, please try again.');
      // 404/409: the order changed underneath us — reload
      if (e?.status === 404 || e?.status === 409) fetchOrders({ quiet: true });
    } finally {
      setPending((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
    }
  };
  //#endregion

  //#region handlers
  const changeView = (value) => {
    setView(value);
    setSkip(0);
  };

  const changeIata = (value) => {
    setIata(value);
    setSkip(0);
  };

  const changeFulfilment = (value) => {
    setFulfilment(value);
    setSkip(0);
  };

  const changeSkip = (value) => {
    setSkip(value);
    tableTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changePageSize = (value) => {
    // keep the first visible row on screen
    setSkip(Math.floor(skip / value) * value);
    setPageSize(value);
  };
  //#endregion

  const filtered = !!(iata || fulfilment || search);

  const pager = (
    <DeskPagination
      total={total}
      skip={skip}
      pageSize={pageSize}
      shown={orders.length}
      disabled={loading}
      onSkipChange={changeSkip}
      onPageSizeChange={changePageSize}
      noun="order"
    />
  );

  return (
    <NewScreen>
      <div className="mt-3" style={{ paddingBottom: 22 }}>
        <Card>
          {/* title + status tabs */}
          <Flex>
            <div>
              <Title>Sunflower Lanyard Orders</Title>
              <Subtitle>
                Orders from the PRM Assist passenger app. Mark an order ready when it can be collected or has been posted.
              </Subtitle>
            </div>
            <div>
              <Toggle color="zinc" defaultValue={view} onValueChange={changeView}>
                {Object.entries(VIEWS).map(([value, { label }]) => (
                  <ToggleItem key={value} value={value} text={label} />
                ))}
              </Toggle>
            </div>
          </Flex>

          {/* filters */}
          <Row className="mt-3 g-2 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label style={styles.label}>Search</Form.Label>
              <Form.Control
                type="search"
                placeholder="Passenger, email, order ID or flight"
                value={searchInput}
                maxLength={MAX_SEARCH_LENGTH}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={styles.label}>Airport</Form.Label>
              <Form.Select value={iata} onChange={(e) => changeIata(e.target.value)}>
                <option value="">All airports</option>
                {AIRPORTS.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={styles.label}>Collection / delivery</Form.Label>
              <Form.Select value={fulfilment} onChange={(e) => changeFulfilment(e.target.value)}>
                <option value="">Collection and delivery</option>
                {FULFILMENTS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md="auto">
              <Button variant="outline-primary" onClick={() => fetchOrders()} disabled={loading}>
                {loading ? <CustomSpinner /> : 'Refresh'}
              </Button>
            </Col>
            <Col xs={12} md style={{ textAlign: 'right' }}>
              {lastUpdated ? (
                <span style={styles.small}>
                  {`Updated ${lastUpdated.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })} · refreshes every minute`}
                </span>
              ) : null}
            </Col>
          </Row>

          {/* table */}
          <div ref={tableTop} className="mt-3">{orders.length > 0 && !error ? pager : null}</div>
          <div style={{ overflowX: 'auto', minHeight: 200 }}>
            {loading && orders.length === 0 ? (
              <div className="d-flex flex-column align-items-center mt-5" style={{ opacity: 0.6 }}>
                <CustomSpinner />
                <p className="mt-3" style={{ color: Colors.PRM_LIGHT_BLUE }}>Getting lanyard orders</p>
              </div>
            ) : error ? (
              <div className="d-flex flex-column align-items-center mt-5">
                <p style={{ color: '#C0392B' }}>{error}</p>
                <Button variant="outline-primary" size="sm" onClick={() => fetchOrders()}>Try again</Button>
              </div>
            ) : orders.length === 0 ? (
              <div className="d-flex flex-column align-items-center mt-5" style={{ opacity: 0.6 }}>
                <p>{filtered ? 'No orders match your search or filters.' : VIEWS[view].empty}</p>
              </div>
            ) : (
              <Table hover responsive style={{ opacity: loading ? 0.6 : 1 }}>
                <thead>
                  <tr>
                    <th><TableHeader title="Ordered" /></th>
                    <th><TableHeader title="Passenger" /></th>
                    <th><TableHeader title="Flight" /></th>
                    <th><TableHeader title="Qty" /></th>
                    <th><TableHeader title="Collection / delivery" /></th>
                    <th><TableHeader title="Notes" /></th>
                    {ACTIONS[view] ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td style={styles.cell}>
                        {formatDateTime(order.createdAt)}
                        <div style={styles.small}>{order.id.slice(0, 8).toUpperCase()}</div>
                      </td>
                      <td style={styles.cell}>
                        {order.passenger?.name || '—'}
                        {order.passenger?.email ? (
                          <div style={styles.small}>
                            <a href={`mailto:${order.passenger.email}`}>{order.passenger.email}</a>
                          </div>
                        ) : null}
                      </td>
                      <td style={styles.cell}>
                        {order.booking?.flightNumber || '—'}
                        {order.booking ? (
                          <div style={styles.small}>
                            {[formatDay(order.booking.departureDate), [order.booking.departureCode, order.booking.arrivalCode].filter(Boolean).join(' → ')]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        ) : null}
                      </td>
                      <td style={styles.cell}>{order.quantity}</td>
                      <td style={styles.cell}>
                        <Badge bg={order.fulfilment === 'delivery' ? 'info' : 'secondary'}>
                          {order.fulfilment === 'delivery' ? 'Delivery' : `Collect · ${order.iata}`}
                        </Badge>
                        <div style={styles.small}>
                          {order.fulfilment === 'delivery' ? order.address : order.collectionPoint}
                        </div>
                      </td>
                      <td style={{ ...styles.cell, maxWidth: 240, whiteSpace: 'pre-wrap' }}>{order.notes || '—'}</td>
                      {ACTIONS[view] ? (
                        <td style={{ ...styles.cell, whiteSpace: 'nowrap' }}>
                          {ACTIONS[view].map((action) => (
                            <Button
                              key={action.status}
                              size="sm"
                              variant={action.variant}
                              style={{ marginRight: 6 }}
                              disabled={!!pending[order.id]}
                              onClick={() => setConfirming({ order, action })}
                            >
                              {actionLabel(action, order)}
                            </Button>
                          ))}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {orders.length > 0 && !error ? pager : null}
        </Card>
      </div>

      {/* confirm dialog */}
      <Modal show={!!confirming} onHide={() => setConfirming(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{confirming ? actionLabel(confirming.action, confirming.order) : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirming ? actionQuestion(confirming.order, confirming.action.status) : ''}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirming(null)}>Back</Button>
          <Button variant={confirming?.action.status === 'cancelled' ? 'danger' : 'primary'} onClick={submitStatus}>
            {confirming ? actionLabel(confirming.action, confirming.order) : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={true} />
    </NewScreen>
  );
};

const styles = {
  label: {
    fontSize: 13,
    opacity: 0.7,
    marginBottom: 2,
  },
  small: {
    fontSize: 12,
    opacity: 0.6,
  },
  cell: {
    verticalAlign: 'top',
  },
};

export default LanyardOrders;
